import React, { useState, useEffect } from 'react';
import { ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { HistoryEntry } from '../types';
import { cn } from '../lib/utils';

export interface MealNotesEditorProps {
  meal: HistoryEntry;
  lang: any;
  theme: 'light' | 'dark';
  onSave: (notes: string) => void;
  defaultOpen?: boolean;
}

export const MealNotesEditor: React.FC<MealNotesEditorProps> = ({ meal, lang, theme, onSave, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(meal?.notes || '');

  useEffect(() => {
    setNotes(meal?.notes || '');
  }, [meal?.id, meal?.notes]);

  const handleSave = () => {
    onSave(notes.trim());
    setIsEditing(false);
  };

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
    setIsEditing(true);
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNotes(meal?.notes || '');
    setIsEditing(false);
  };

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleSave();
  };

  const hasNotes = Boolean(meal?.notes && meal.notes.trim().length > 0);

  return (
    <div className={cn(
      "p-4 rounded-2xl border transition-all mt-2",
      theme === 'light' 
        ? "bg-zinc-50 border-zinc-200/60" 
        : "bg-zinc-900/45 border-[var(--card-border)]/70"
    )}>
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between cursor-pointer select-none group touch-manipulation min-h-[32px]"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <ClipboardList className="w-4 h-4 text-emerald-500 shrink-0" />
          <div className="flex items-center gap-2 min-w-0">
            <span className={cn("text-[10px] font-bold uppercase tracking-widest truncate", theme === 'light' ? "text-zinc-600" : "text-zinc-400")}>
              {lang === 'ua' ? 'Нотатки до страви' : 'Meal Notes'}
            </span>
            {hasNotes && (
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" title={lang === 'ua' ? 'Є нотатка' : 'Has note'} />
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {!isEditing ? (
            <>
              <button
                type="button"
                onClick={handleStartEdit}
                className="text-[10px] font-bold uppercase tracking-widest text-emerald-500 hover:text-emerald-600 transition-colors cursor-pointer px-1 py-0.5 rounded"
              >
                {hasNotes ? (lang === 'ua' ? 'Редагувати' : 'Edit') : (lang === 'ua' ? 'Додати нотатку' : 'Add Note')}
              </button>
              <div className="p-1 text-zinc-400 group-hover:text-zinc-600 dark:group-hover:text-zinc-200 transition-colors">
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-500 transition-colors cursor-pointer px-1 py-0.5"
              >
                {lang === 'ua' ? 'Скасувати' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleSaveClick}
                className="text-[10px] font-bold uppercase tracking-widest text-emerald-500 hover:text-emerald-600 transition-colors font-bold cursor-pointer px-1 py-0.5"
              >
                {lang === 'ua' ? 'Зберегти' : 'Save'}
              </button>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden pt-2.5 border-t border-zinc-200/50 dark:border-zinc-800/60 mt-2"
          >
            {!isEditing ? (
              <div className="min-h-[44px] flex flex-col justify-center py-1">
                <p className={cn(
                  "text-sm leading-relaxed whitespace-pre-wrap font-medium",
                  meal?.notes 
                    ? (theme === 'light' ? "text-zinc-700" : "text-zinc-300")
                    : "text-zinc-400 dark:text-zinc-500 italic placeholder:italic"
                )}>
                  {meal?.notes || (lang === 'ua' ? 'Немає нотаток для цієї страви' : 'No notes for this meal yet')}
                </p>
              </div>
            ) : (
              <div className="space-y-2 pt-1">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={lang === 'ua' ? 'Введіть ваші нотатки...' : 'Enter your notes...'}
                  autoFocus
                  className="w-full h-24 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl px-3 py-2 outline-none focus:border-emerald-500 transition-colors text-sm text-[var(--text)] resize-none placeholder:italic"
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
